import React, { useEffect, useState, useCallback } from 'react';
import { Share2, Sparkles, Loader2, Copy, Check, Trash2, Calendar, Send } from 'lucide-react';
import { suiteAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Panel, PanelLoader, EmptyState, Card, Field, Chip, inputCls, copyText } from './shared';
import { toast } from 'sonner';

const PLATFORMS = ['instagram', 'twitter', 'linkedin', 'facebook', 'tiktok', 'threads', 'youtube'];

export default function SocialPanel({ campaigns = [] }) {
  const [posts, setPosts] = useState([]);
  const [limits, setLimits] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [brief, setBrief] = useState('');
  const [selected, setSelected] = useState(['instagram', 'twitter', 'linkedin']);
  const [copied, setCopied] = useState(null);
  const [campaignId, setCampaignId] = useState('');
  const [accounts, setAccounts] = useState(null);
  const [manual, setManual] = useState({ platform: 'instagram', content: '', scheduled_at: '' });

  const load = useCallback(() => {
    suiteAPI.listSocial({}).then(r => { setPosts(r.data.posts); setLimits(r.data.limits); }).finally(() => setLoading(false));
    suiteAPI.socialAccounts().then(r => setAccounts(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    if (!brief.trim()) return toast.error('What should we post about?');
    setBusy('ai');
    try {
      await suiteAPI.aiSocial({ brief, platforms: selected, campaign_id: campaignId || null });
      toast.success('Posts drafted for every platform');
      setBrief(''); load();
    } catch { toast.error('Generation failed'); }
    finally { setBusy(''); }
  };

  const addManual = async () => {
    if (!manual.content.trim()) return toast.error('Write your post');
    await suiteAPI.createSocialPost({ ...manual, campaign_id: campaignId || null });
    setManual({ platform: 'instagram', content: '', scheduled_at: '' });
    load();
  };

  const copyPost = (p) => {
    copyText(`${p.content}\n\n${(p.hashtags || []).join(' ')}`);
    setCopied(p.id);
    toast.success('Copied — paste it straight into the app');
    setTimeout(() => setCopied(null), 1500);
  };

  const publishNow = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await suiteAPI.publishSocial(p.id);
      toast.success(data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Publishing failed'); }
    finally { setBusy(''); }
  };

  return (
    <Panel title="Social Studio" subtitle="Platform-native copy, a scheduling queue and automatic publishing to any connected account. Scheduled posts go out on their own every couple of minutes." testId="suite-social"
      action={
        <div className="flex gap-2 items-center flex-wrap">
          {accounts && Object.entries(accounts).map(([k, v]) => (
            <span key={k} className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${v.configured ? 'bg-[#b8ff00]/15 text-[#b8ff00]' : 'bg-zinc-800 text-zinc-500'}`} data-testid={`social-acct-${k}`}>{k}</span>
          ))}
          <Button onClick={() => suiteAPI.processAutomations().then(r => { toast.success(`${r.data.social_published} published · ${r.data.social_ready_to_post} awaiting`); load(); })} className="bg-zinc-800 text-white" data-testid="social-process"><Send size={14} className="mr-1.5" /> Run queue now</Button>
        </div>
      }>
      <div className="grid lg:grid-cols-[400px_1fr] gap-5">
        <div className="space-y-3">
          <Card className="p-5 space-y-3">
            <p className="text-sm font-bold text-white flex items-center gap-1.5"><Sparkles size={14} className="text-[#b8ff00]" /> AI composer</p>
            <Field label="What's the post about?">
              <Textarea value={brief} onChange={e => setBrief(e.target.value)} rows={3} placeholder="Teaser for our September jacket drop — 200 units, 3 colours, waitlist opens Friday" className={inputCls} data-testid="social-brief" />
            </Field>
            <Field label="Platforms">
              <div className="flex flex-wrap gap-1.5">
                {PLATFORMS.map(p => (
                  <Chip key={p} active={selected.includes(p)} testId={`social-platform-${p}`}
                    onClick={() => setSelected(s => s.includes(p) ? s.filter(x => x !== p) : [...s, p])}>{p}</Chip>
                ))}
              </div>
            </Field>
            <Field label="Campaign">
              <select value={campaignId} onChange={e => setCampaignId(e.target.value)} className="w-full bg-zinc-800 border border-zinc-700 rounded-md text-white text-sm px-3 py-2" data-testid="social-campaign">
                <option value="">None</option>
                {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Button onClick={generate} disabled={busy === 'ai'} className="w-full bg-violet-500 text-white" data-testid="social-generate">
              {busy === 'ai' ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />} Generate posts
            </Button>
          </Card>

          <Card className="p-5 space-y-3">
            <p className="text-sm font-bold text-white">Write it yourself</p>
            <Field label="Platform">
              <select value={manual.platform} onChange={e => setManual({ ...manual, platform: e.target.value })} className="w-full bg-zinc-800 border border-zinc-700 rounded-md text-white text-sm px-3 py-2">
                {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Post" hint={`${manual.content.length}/${limits[manual.platform] || 2200} characters`}>
              <Textarea value={manual.content} onChange={e => setManual({ ...manual, content: e.target.value })} rows={4} className={inputCls} data-testid="social-manual-content" />
            </Field>
            <Field label="Schedule for"><Input type="datetime-local" value={manual.scheduled_at} onChange={e => setManual({ ...manual, scheduled_at: e.target.value })} className={inputCls} data-testid="social-schedule" /></Field>
            <Button onClick={addManual} className="w-full bg-[#b8ff00] text-black font-bold" data-testid="social-add">Add to queue</Button>
          </Card>
        </div>

        <div className="space-y-3">
          {loading ? <PanelLoader /> : posts.length === 0 ? <EmptyState icon={Share2} text="No posts yet. Draft your first batch with AI." /> : posts.map(p => (
            <Card key={p.id} className="p-4" data-testid={`social-post-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 capitalize">{p.platform}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${p.status === 'posted' ? 'bg-[#b8ff00]/15 text-[#b8ff00]' : p.status === 'ready_to_post' ? 'bg-amber-500/15 text-amber-400' : 'bg-zinc-800 text-zinc-500'}`}>{p.status?.replace(/_/g, ' ')}</span>
                    {p.scheduled_at && <span className="text-[10px] text-zinc-500 flex items-center gap-1"><Calendar size={10} /> {p.scheduled_at.slice(0, 16).replace('T', ' ')}</span>}
                    <span className={`text-[10px] ml-auto ${p.content.length > (limits[p.platform] || 2200) ? 'text-red-400' : 'text-zinc-600'}`}>{p.content.length}/{limits[p.platform] || 2200}</span>
                  </div>
                  {p.hook && <p className="text-[11px] text-[#b8ff00] mb-1">Hook: {p.hook}</p>}
                  <p className="text-sm text-zinc-300 whitespace-pre-wrap">{p.content}</p>
                  {(p.hashtags || []).length > 0 && <p className="text-[11px] text-cyan-400 mt-2">{p.hashtags.join(' ')}</p>}
                  {p.best_time && <p className="text-[11px] text-zinc-500 mt-1">Best time: {p.best_time}</p>}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <Button size="sm" disabled={busy === p.id} onClick={() => publishNow(p)} className="bg-[#b8ff00] text-black text-[11px] font-bold" data-testid={`social-publish-${p.id}`}>
                    {busy === p.id ? <Loader2 size={12} className="animate-spin" /> : 'Publish'}
                  </Button>
                  <Button size="sm" variant="ghost" className="text-zinc-400" onClick={() => copyPost(p)} data-testid={`social-copy-${p.id}`}>{copied === p.id ? <Check size={13} /> : <Copy size={13} />}</Button>
                  <select value={p.status} onChange={e => suiteAPI.updateSocial(p.id, { status: e.target.value }).then(load)} className="bg-zinc-800 border border-zinc-700 rounded text-[10px] text-zinc-300 px-1 py-1" data-testid={`social-status-${p.id}`}>
                    {['draft', 'scheduled', 'ready_to_post', 'posted', 'failed'].map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                  </select>
                  {p.publish_result?.error && <span className="text-[10px] text-red-400 max-w-[120px] truncate" title={p.publish_result.error}>{p.publish_result.error}</span>}
                  <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-400" onClick={() => suiteAPI.deleteSocial(p.id).then(load)}><Trash2 size={13} /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </Panel>
  );
}
